import { messaging } from '../firebase/config';
import { getToken, onMessage, MessagePayload } from 'firebase/messaging';
import { doc, setDoc, getDoc, deleteDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { DailyReminder, NotificationPermissionState, FCMTokenData } from '../types/user';
import { DateTime } from 'luxon';
import { userService } from '../services/userServices';

class NotificationService {
  private vapidKeyProduction = 'BFZyYdaWbWYFGl_0-5cErKaiAE_bYFep8q8rNHTpoEm5R09COuKfdaHVSrH4znguViqP1RLs8sBcOsYd2fEB6eA';
  private vapidKeyStaging = "BI4kNxZ_eZBSbpJKvLTS8c7oMesW-qBggrvL8N7l-XmoW0rv9LqmAeYE9Smgd_lKSQ3p3r7xLt80tPZ1fKcxe-8";

  // Get the appropriate VAPID key based on environment
  private getVapidKey(): string {
    const isProduction = process.env.REACT_APP_FORCE_PRODUCT_MODE === 'true' || (typeof window !== 'undefined' && (window.location.hostname === 'peakxv2.web.app'  || window.location.hostname === 'peak.app' || window.location.hostname === 'www.peak.app'));
    return isProduction ? this.vapidKeyProduction : this.vapidKeyStaging;
  }

  // Get current device ID (public method for checking device registration)
  getCurrentDeviceId(): string {
    // Try to get persistent device ID from localStorage first
    const storedDeviceId = localStorage.getItem('peakx_device_id');
    if (storedDeviceId) {
      return storedDeviceId;
    }
    
    // Generate new device ID based on stable characteristics
    const userAgent = navigator.userAgent;
    const platform = navigator.platform;
    const language = navigator.language;
    const screenInfo = `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    // Create a more unique device fingerprint
    const deviceFingerprint = `${userAgent}|${platform}|${language}|${screenInfo}|${timezone}`;
    const deviceId = btoa(deviceFingerprint).slice(0, 20); // Increased length for uniqueness
    
    // Store for future use
    localStorage.setItem('peakx_device_id', deviceId);
    
    return deviceId;
  }

  // Generate device information for FCM token identification
  private generateDeviceInfo(): { deviceId: string; deviceName: string; platform: string; userAgent: string } {
    const userAgent = navigator.userAgent;
    
    // Use the same enhanced device ID generation as getCurrentDeviceId
    const deviceId = this.getCurrentDeviceId();
    
    // Extract browser name
    let browserName = 'Unknown Browser';
    if (userAgent.includes('Chrome')) browserName = 'Chrome';
    else if (userAgent.includes('Firefox')) browserName = 'Firefox';
    else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) browserName = 'Safari';
    else if (userAgent.includes('Edge')) browserName = 'Edge';
    
    // Extract OS name
    let osName = 'Unknown OS';
    if (userAgent.includes('Windows')) osName = 'Windows';
    else if (userAgent.includes('Mac')) osName = 'macOS';
    else if (userAgent.includes('Linux')) osName = 'Linux';
    else if (userAgent.includes('Android')) osName = 'Android';
    else if (userAgent.includes('iOS')) osName = 'iOS';
    
    const deviceName = `${browserName} on ${osName}`;
    const platform = 'web';
    
    return { deviceId, deviceName, platform, userAgent };
  }
  
  // Check if notifications are supported
  isSupported(): boolean {
    return 'Notification' in window && 'serviceWorker' in navigator && !!messaging;
  }

  // Get current notification permission state
  getPermissionState(): NotificationPermissionState {
    if (!this.isSupported()) {
      return { granted: false, denied: true, default: false };
    }

    const permission = Notification.permission;
    return {
      granted: permission === 'granted',
      denied: permission === 'denied',
      default: permission === 'default'
    };
  }

  // Request notification permission
  async requestPermission(): Promise<boolean> {
    if (!this.isSupported()) {
      throw new Error('Notifications are not supported in this browser');
    }

    if (Notification.permission === 'granted') {
      return true;
    }

    if (Notification.permission === 'denied') {
      throw new Error('Notifications are blocked. Please enable them in your browser settings.');
    }

    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }

  // Get FCM token
  async getFCMToken(): Promise<string | null> {
    if (!messaging) {
      console.warn('Firebase messaging not initialized');
      return null;
    }

    try {
      // Ensure the dedicated Firebase Messaging service worker is registered and used.
      // This avoids regressions after app SW updates where the default registration
      // might point to the app SW that doesn't include messaging handlers.
      let swReg: ServiceWorkerRegistration | undefined;
      if ('serviceWorker' in navigator) {
        try {
          // Try to get existing registration first
          swReg = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js') || undefined;
          // If not found, register explicitly
          if (!swReg) {
            swReg = await navigator.serviceWorker.register('/firebase-messaging-sw.js', { scope: '/' });
          }
        } catch (err) {
          console.warn('Could not obtain/register Firebase Messaging SW, falling back to default registration:', err);
        }
      }

      const vapidKey = this.getVapidKey();
      const token = await getToken(messaging, {
        vapidKey,
        // Prefer the dedicated messaging SW registration when available
        ...(swReg ? { serviceWorkerRegistration: swReg } as any : {})
      });
      console.log(`🔔 FCM token generated using ${vapidKey === this.vapidKeyProduction ? 'PRODUCTION' : 'STAGING'} VAPID key`);
      return token;
    } catch (error) {
      console.error('Failed to get FCM token:', error);
      return null;
    }
  }

  // Add or update FCM token in user's token list
  async addFCMTokenToUser(userId: string, enableNotifications: boolean = true): Promise<FCMTokenData | null> {
    const token = await this.getFCMToken();
    if (!token) {
      throw new Error('Failed to get FCM token');
    }

    const deviceInfo = this.generateDeviceInfo();
    const now = new Date();

    const tokenData: FCMTokenData = {
      token,
      deviceId: deviceInfo.deviceId,
      deviceName: deviceInfo.deviceName,
      platform: deviceInfo.platform,
      userAgent: deviceInfo.userAgent,
      enabled: enableNotifications,
      createdAt: now,
      lastUsed: now
    };

    try {
      await userService.addFCMTokenToUser(userId, tokenData);
      console.log(`FCM token added/updated for user ${userId} on device ${deviceInfo.deviceName}`);
      return tokenData;
    } catch (error) {
      console.error('Failed to add FCM token to user:', error);
      throw error;
    }
  }

  // Get all enabled FCM tokens for a user
  async getEnabledFCMTokens(userId: string): Promise<string[]> {
    try {
      return await userService.getEnabledFCMTokens(userId);
    } catch (error) {
      console.error('Failed to get enabled FCM tokens:', error);
      return [];
    }
  }

  // Remove invalid FCM tokens from user's list
  async removeInvalidFCMToken(userId: string, invalidToken: string): Promise<void> {
    try {
      await userService.removeInvalidFCMToken(userId, invalidToken);
      console.log(`Removed invalid FCM token for user ${userId}`);
    } catch (error) {
      console.error('Failed to remove invalid FCM token:', error);
    }
  }

  // Toggle notification enable/disable for a specific device
  async toggleDeviceNotifications(userId: string, deviceId: string, enabled: boolean): Promise<void> {
    try {
      await userService.toggleDeviceNotifications(userId, deviceId, enabled);
      console.log(`${enabled ? 'Enabled' : 'Disabled'} notifications for device ${deviceId}`);
    } catch (error) {
      console.error('Failed to toggle device notifications:', error);
      throw error;
    }
  }

  // Get user's FCM tokens list for management UI
  async getUserFCMTokens(userId: string): Promise<FCMTokenData[]> {
    try {
      const tokens = await userService.getUserFCMTokens(userId);
      console.log(`[NotificationService] Retrieved ${tokens.length} FCM tokens for user ${userId}`);
      return tokens;
    } catch (error) {
      console.error('Failed to get user FCM tokens:', error);
      return [];
    }
  }

  // Debug function to analyze device ID generation
  debugDeviceInfo(): void {
    const currentDeviceId = this.getCurrentDeviceId();
    const deviceInfo = this.generateDeviceInfo();
    
    console.log('=== FCM Token Debug Info ===');
    console.log('Current Device ID:', currentDeviceId);
    console.log('Device Info:', deviceInfo);
    console.log('User Agent:', navigator.userAgent);
    console.log('Platform:', navigator.platform);
    console.log('Language:', navigator.language);
    console.log('Screen:', `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`);
    console.log('Timezone:', Intl.DateTimeFormat().resolvedOptions().timeZone);
    console.log('=============================');
  }

  // Convert local time to GMT considering timezone
  convertToGMT(localHour: number, timezone: string): number {
    // Create a DateTime object with the current date and the specified local hour in the given timezone
    const localDateTime = DateTime.now()
      .setZone(timezone)
      .set({ hour: localHour, minute: 0, second: 0, millisecond: 0 });
    
    // Convert to UTC and return the UTC hour
    return localDateTime.toUTC().hour;
  }

  // Get user's timezone
  getUserTimezone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  // Save reminder to Firestore
  async saveDailyReminder(userId: string, localHour: number): Promise<void> {
    if (!messaging) {
      throw new Error('Firebase messaging not initialized');
    }

    // Delete any existing reminder first
    await this.deleteReminder(userId);

    // Ensure the current device has an FCM token registered and enabled
    await this.addFCMTokenToUser(userId, true);

    // Get all enabled FCM tokens for this user
    const enabledTokens = await this.getEnabledFCMTokens(userId);
    if (enabledTokens.length === 0) {
      throw new Error('No enabled notification tokens found. Please enable notifications on at least one device.');
    }

    const timezone = this.getUserTimezone();
    const gmtHour = this.convertToGMT(localHour, timezone);

    const reminderData: Omit<DailyReminder, 'createdAt' | 'updatedAt'> = {
      userId,
      localHour,
      gmtHour,
      timezone,
      enabled: true
    };

    // Save to reminders collection with GMT hour as document ID structure
    const reminderRef = doc(db, 'reminders', gmtHour.toString(), 'users', userId);
    await setDoc(reminderRef, {
      ...reminderData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    // Store GMT hour reference in user document for efficient lookup
    const userRef = doc(db, 'users', userId);
    await setDoc(
      userRef,
      {
        reminderGmtHour: gmtHour,
        updatedAt: serverTimestamp()
      },
      { merge: true }
    );
    
    console.log(`Reminder set for ${localHour}:00 local time (${gmtHour}:00 GMT) with ${enabledTokens.length} enabled device(s)`);
  }

  // Get user's current reminder
  async getUserReminder(userId: string): Promise<DailyReminder | null> {
    try {
      // First check if user has a reminder reference stored
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists() && userDoc.data().reminderGmtHour !== undefined) {
        const gmtHour = userDoc.data().reminderGmtHour;
        const reminderRef = doc(db, 'reminders', gmtHour.toString(), 'users', userId);
        const reminderDoc = await getDoc(reminderRef);
        
        if (reminderDoc.exists()) {
          const data = reminderDoc.data();
          return {
            ...data,
            createdAt: data.createdAt?.toDate(),
            updatedAt: data.updatedAt?.toDate()
          } as DailyReminder;
        }
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching user reminder:', error);
      return null;
    }
  }

  // Delete user's reminder
  async deleteReminder(userId: string): Promise<void> {
    try {
      // First check if user has a reminder reference stored
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists() && userDoc.data().reminderGmtHour !== undefined) {
        const gmtHour = userDoc.data().reminderGmtHour;
        const reminderRef = doc(db, 'reminders', gmtHour.toString(), 'users', userId);
        
        // Delete the reminder document
        await deleteDoc(reminderRef);
        
        // Remove GMT hour reference from user document
        await updateDoc(userRef, {
          reminderGmtHour: null,
          updatedAt: serverTimestamp()
        });
        
        console.log(`Reminder deleted for GMT hour ${gmtHour}`);
        return;
      }
      
      // Fallback: if no reference found, search all GMT hours (for backwards compatibility)
      for (let gmtHour = 0; gmtHour < 24; gmtHour++) {
        const reminderRef = doc(db, 'reminders', gmtHour.toString(), 'users', userId);
        const reminderDoc = await getDoc(reminderRef);
        
        if (reminderDoc.exists()) {
          await deleteDoc(reminderRef);
          // Also update user document to remove reference
          await updateDoc(userRef, {
            reminderGmtHour: null,
            updatedAt: serverTimestamp()
          });
          console.log(`Reminder deleted for GMT hour ${gmtHour} (fallback method)`);
          return;
        }
      }
    } catch (error) {
      console.error('Error deleting user reminder:', error);
    }
  }

  // Setup foreground message listener
  setupForegroundMessageListener(callback: (payload: MessagePayload) => void): (() => void) | null {
    if (!messaging) {
      console.warn('Firebase messaging not initialized');
      return null;
    }

    return onMessage(messaging, callback);
  }
}

export const notificationService = new NotificationService();
export default notificationService; 